<div class="required-notifications-backdrop" *ngIf="shouldShowModal$ | async">
  <section class="required-notifications-modal" role="dialog" aria-modal="true"
    aria-labelledby="requiredNotificationsTitle">
    <div class="modal-head">
      <button type="button" class="modal-close-btn" (click)="closeModal()" aria-label="Close notifications">
        ✕
      </button>

      <div>
        <h2 id="requiredNotificationsTitle">Required approvals</h2>
        <p>
          You must approve, reject, or request changes for all required notifications before using the application.
        </p>
      </div>

      <div class="week-pill" *ngIf="currentFullYearWeek$ | async as week">
        Week {{ week }}
      </div>
    </div>

    <ng-container *ngIf="notifications$ | async as notifications">
      <div class="summary-row">
        <div class="summary-card">
          <span class="summary-label">Total notifications</span>
          <strong>{{ getTotalCount(notifications) }}</strong>
        </div>

        <div class="summary-card">
          <span class="summary-label">Pending actions</span>
          <strong>{{ getPendingActionCount(notifications) }}</strong>
        </div>

        <button type="button" class="ghost-btn" (click)="refresh()"
          [disabled]="(isLoading$ | async) || (isActionRunning$ | async)">
          Refresh
        </button>

        <button type="button" class="primary-btn" (click)="approveAll()"
          [disabled]="!getPendingActionCount(notifications) || (isLoading$ | async) || (isActionRunning$ | async)">
          Approve All
        </button>
      </div>

      <div class="inline-error" *ngIf="errorMessage$ | async as errorMessage">
        {{ errorMessage }}
      </div>

      <div class="modal-body">
        <section class="notification-section" *ngIf="notifications.dayOffRequestGroupsNeedToApproveBySupervisor.length">
          <div class="section-head">
            <h3>Time off requests</h3>
            <span>{{ notifications.dayOffRequestGroupsNeedToApproveBySupervisor.length }}</span>
          </div>

          <article class="notification-card"
            *ngFor="let group of notifications.dayOffRequestGroupsNeedToApproveBySupervisor; trackBy: trackById">
            <div class="card-main">
              <div class="card-title">
                {{ getEmployeeName(group.employee) }}
                <span class="muted">#{{ group.employee.employeeNumber || '—' }}</span>
              </div>

              <div class="card-sub">
                {{ group.payCode.name }} • {{ getDayOffTotalHours(group) | number: '1.2-2' }}h
              </div>

              <div class="card-meta">
                {{ getDayOffDatesLabel(group) }}
              </div>

              <div class="reason" *ngIf="group.reason">
                {{ group.reason }}
              </div>
            </div>

            <div class="card-actions" *ngIf="isDayOffActionable(group); else resolvedDayOff">
              <button type="button" class="danger-btn" (click)="rejectDayOff(group)"
                [disabled]="isActionRunning$ | async">
                Reject
              </button>

              <button type="button" class="primary-btn" (click)="approveDayOff(group)"
                [disabled]="isActionRunning$ | async">
                Approve
              </button>
            </div>

            <ng-template #resolvedDayOff>
              <div class="resolved-pill" [class.approved]="group.isApproved === true"
                [class.rejected]="group.isApproved === false">
                {{ group.isApproved === true ? 'Approved' : 'Rejected' }}
              </div>
            </ng-template>
          </article>
        </section>

        <section class="notification-section" *ngIf="notifications.bookedHoursNeedToApproveBySupervisor.length">
          <div class="section-head">
            <h3>Booked hours needing supervisor approval</h3>
            <span>{{ notifications.bookedHoursNeedToApproveBySupervisor.length }}</span>
          </div>

          <article class="notification-card compact"
            *ngFor="let row of notifications.bookedHoursNeedToApproveBySupervisor; trackBy: trackById">
            <div class="card-main">
              <div class="card-title">
                {{ getEmployeeName(row.employee) }}
                <span class="muted">#{{ row.employee.employeeNumber || '—' }}</span>
              </div>

              <div class="card-sub">
                {{ row.bookedDate | date: 'shortDate' }}
                • {{ row.hours | number: '1.2-2' }}h
                • {{ row.payCode?.name || '—' }}
                • Week {{ getBookedHourYyww(row) }}
              </div>

              <div class="card-meta compact-details">
                <span>{{ getBookedHourCompany(row) }}</span>
                <span>{{ getBookedHourDepartment(row) }}</span>
                <span>{{ getBookedHourWorkerType(row) }}</span>
              </div>

              <div class="card-meta">
                {{ row.workOrderNumber?.won || '—' }}
                <span *ngIf="row.workOrderNumber?.description"> — {{ row.workOrderNumber?.description }}</span>
              </div>
            </div>

            <div class="card-actions wide-actions">
              <button type="button" class="danger-btn" (click)="rejectSupervisorBookedHour(row)"
                [disabled]="isActionRunning$ | async">
                Reject
              </button>

              <button type="button" class="ghost-btn" (click)="openSupervisorBookedHourChange(row)"
                [disabled]="isActionRunning$ | async">
                Change Hours
              </button>

              <button type="button" class="primary-btn" (click)="approveSupervisorBookedHour(row)"
                [disabled]="isActionRunning$ | async">
                Approve
              </button>
            </div>
          </article>
        </section>

        <section class="notification-section" *ngIf="notifications.bookedHoursNeedToApproveBySelf.length">
          <div class="section-head">
            <h3>Booked hours needing your approval</h3>
            <span>{{ notifications.bookedHoursNeedToApproveBySelf.length }}</span>
          </div>

          <article class="notification-card compact"
            *ngFor="let row of notifications.bookedHoursNeedToApproveBySelf; trackBy: trackById">
            <div class="card-main">
              <div class="card-title">
                {{ getEmployeeName(row.employee) }}
                <span class="muted">#{{ row.employee.employeeNumber || '—' }}</span>
              </div>

              <div class="card-sub">
                {{ row.bookedDate | date: 'shortDate' }}
                • {{ row.hours | number: '1.2-2' }}h
                • {{ row.payCode?.name || '—' }}
                • Week {{ getBookedHourYyww(row) }}
              </div>

              <div class="card-meta compact-details">
                <span>{{ getBookedHourCompany(row) }}</span>
                <span>{{ getBookedHourDepartment(row) }}</span>
                <span>{{ getBookedHourWorkerType(row) }}</span>
              </div>

              <div class="card-meta">
                {{ row.workOrderNumber?.won || '—' }}
                <span *ngIf="row.workOrderNumber?.description"> — {{ row.workOrderNumber?.description }}</span>
              </div>
            </div>

            <div class="card-actions wide-actions">
              <button type="button" class="danger-btn" (click)="rejectSelfBookedHour(row)"
                [disabled]="isActionRunning$ | async">
                Reject
              </button>

              <button type="button" class="ghost-btn" (click)="openSelfBookedHourChange(row)"
                [disabled]="isActionRunning$ | async">
                Request Change
              </button>

              <button type="button" class="primary-btn" (click)="approveSelfBookedHour(row)"
                [disabled]="isActionRunning$ | async">
                Approve
              </button>
            </div>
          </article>
        </section>
      </div>
    </ng-container>

    <div class="modal-busy" *ngIf="(isLoading$ | async) || (isActionRunning$ | async)">
      <div class="spinner"></div>
    </div>
  </section>
</div>

<div class="change-hours-backdrop" *ngIf="bookedHourChangeModal as modal">
  <section class="change-hours-modal" role="dialog" aria-modal="true" aria-labelledby="changeHoursTitle">
    <div class="change-head">
      <div>
        <h3 id="changeHoursTitle">Change booked hours</h3>
        <p>Update the requested hours for this booked-hour record.</p>
      </div>
    </div>

    <div class="change-summary">
      <div>
        <span>Employee</span>
        <strong>{{ getEmployeeName(modal.row.employee) }}</strong>
      </div>

      <div>
        <span>Date</span>
        <strong>{{ modal.row.bookedDate | date: 'shortDate' }}</strong>
      </div>

      <div>
        <span>Pay Code</span>
        <strong>{{ modal.row.payCode?.name || '—' }}</strong>
      </div>

      <div>
        <span>Week</span>
        <strong>{{ getBookedHourYyww(modal.row) }}</strong>
      </div>

      <div>
        <span>Company</span>
        <strong>{{ getBookedHourCompany(modal.row) }}</strong>
      </div>

      <div>
        <span>Department</span>
        <strong>{{ getBookedHourDepartment(modal.row) }}</strong>
      </div>

      <div>
        <span>Worker Type</span>
        <strong>{{ getBookedHourWorkerType(modal.row) }}</strong>
      </div>

      <div>
        <span>WON</span>
        <strong>{{ modal.row.workOrderNumber?.won || '—' }}</strong>
      </div>
    </div>

    <label class="change-field">
      <span>New booked hours</span>
      <input type="number" min="0" step="0.25" inputmode="decimal" [ngModel]="modal.newBookedHours"
        (ngModelChange)="onChangeHoursInput($event)" />
    </label>

    <label class="bypass-row" *ngIf="!modal.isSelfApproval">
      <input type="checkbox" [ngModel]="modal.bypassEmployeeApproval"
        (ngModelChange)="onBypassEmployeeApprovalChange($event)" />
      <span>Bypass employee approval and approve these changed hours immediately</span>
    </label>

    <div class="change-note" *ngIf="modal.isSelfApproval">
      This will request a changed hour value. It will not supervisor-approve the record.
    </div>

    <div class="inline-error local" *ngIf="modal.errorMessage">
      {{ modal.errorMessage }}
    </div>

    <div class="change-actions">
      <button type="button" class="ghost-btn" (click)="closeBookedHourChangeModal()">
        Cancel
      </button>

      <button type="button" class="primary-btn" (click)="submitBookedHourChange()"
        [disabled]="isActionRunning$ | async">
        Continue
      </button>
    </div>
  </section>
</div>

<div class="confirm-backdrop" *ngIf="confirmationAction">
  <section class="confirm-modal" role="dialog" aria-modal="true" aria-labelledby="confirmNotificationTitle">
    <h3 id="confirmNotificationTitle">{{ getConfirmationTitle() }}</h3>

    <p>
      {{ getConfirmationMessage() }}
    </p>

    <div class="confirm-actions">
      <button type="button" class="ghost-btn" (click)="cancelConfirmation()">
        Cancel
      </button>

      <button type="button" class="primary-btn" [class.danger-confirm]="isConfirmationDanger()"
        (click)="confirmAction()" [disabled]="isActionRunning$ | async">
        {{ getConfirmationButtonText() }}
      </button>
    </div>
  </section>
</div>